import { useEffect, useState, useCallback, useMemo } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { adminApi } from "@/lib/api";
import { Toaster, toast } from "sonner";
import {
  RefreshCw, Download, BarChart3, CheckCircle2, DollarSign, XCircle, TrendingUp,
  TrendingDown, Percent, Users, Repeat, ArrowRight, Zap, Award, Sparkles,
} from "lucide-react";

const RANGES = [
  { v: 7, label: "7 days" },
  { v: 30, label: "30 days" },
  { v: 90, label: "90 days" },
];

const BRAND_COLORS = ["bg-indigo-500", "bg-sky-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500", "bg-violet-500", "bg-teal-500", "bg-orange-500", "bg-lime-500", "bg-indigo-500"];
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function fmtTime(iso) { if (!iso) return "—"; return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }); }
function fmtDayLabel(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

// SVG sparkline
function Sparkline({ points, color = "#8b5cf6", height = 60 }) {
  if (!points || points.length === 0) return null;
  const w = 100, h = height;
  const max = Math.max(...points, 1);
  const step = w / Math.max(points.length - 1, 1);
  const pts = points.map((p, i) => `${(i * step).toFixed(2)},${(h - (p / max) * (h - 4) - 2).toFixed(2)}`).join(" ");
  const area = `M0,${h} L${pts.split(" ").join(" L")} L${w},${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-full">
      <path d={area} fill={color} opacity="0.15" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function Delta({ v, invert }) {
  if (v === null || v === undefined || v === 0) return <span className="text-slate-400 text-xs font-black">—</span>;
  const up = v > 0;
  const good = invert ? !up : up;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-black ${good ? "text-emerald-600" : "text-rose-600"}`}>
      <Icon size={11} strokeWidth={2.5} />
      {Math.abs(v)}%
    </span>
  );
}

function Bubble({ n, color, muted }) {
  const map = {
    sky: muted ? "bg-sky-50 text-sky-700" : "bg-sky-100 text-sky-700",
    orange: "bg-orange-50 text-orange-600",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-600",
    violet: "bg-violet-50 text-violet-700",
  }[color] || "bg-slate-100 text-slate-700";
  return <span className={`inline-flex items-center justify-center min-w-[36px] h-7 px-2 rounded-full text-xs font-black tabular-nums ${map}`}>{n}</span>;
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/analytics/summary", { params: { days } });
      setData(r.data);
    } catch { toast.error("Failed to load analytics"); }
    finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const exportCSV = useCallback(async () => {
    try {
      const r = await adminApi.get("/admin/submissions/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = `gcnexon-analytics-${days}d.csv`; a.click();
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed"); }
  }, [days]);

  const trend = useMemo(() => (data?.daily ? [...data.daily].reverse().map((d) => d.payout || d.value) : []), [data]);
  const brandMax = useMemo(() => Math.max(...(data?.top_brands || []).map((b) => b.count), 1), [data]);
  const peakMax = useMemo(() => {
    if (!data?.peak_hours) return 1;
    let m = 0;
    data.peak_hours.forEach((row) => row.forEach((v) => { if (v > m) m = v; }));
    return m || 1;
  }, [data]);

  if (!data) return <AdminLayout><div className="p-8 text-slate-400">Loading analytics…</div></AdminLayout>;

  const funnelMax = Math.max(...data.funnel.map((f) => f.value), 1);

  return (
    <AdminLayout>
      <Toaster richColors position="top-center" />
      <div className="p-6 md:p-8" data-testid="admin-analytics">

        {/* Header + range switcher */}
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <span className="text-[10px] font-black tracking-wider uppercase text-indigo-600">Analytics</span>
            <h1 className="text-3xl font-black tracking-tight text-slate-900 mt-1" data-testid="analytics-title">Dashboard Analytics</h1>
            <p className="text-slate-500 mt-1 text-sm">Overview of submissions, revenue &amp; performance · <span className="text-slate-400">last {days} days</span> · updated {fmtTime(data.generated_at)}</p>
            <div className="mt-3 h-[3px] w-24 rounded-full bg-gradient-to-r from-indigo-500 via-indigo-600 to-emerald-400" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
              {RANGES.map((r) => (
                <button key={r.v} onClick={() => setDays(r.v)} data-testid={`analytics-range-${r.v}`}
                  className={`px-3 h-9 rounded-lg text-xs font-black transition ${days === r.v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
                  {r.label}
                </button>
              ))}
            </div>
            <button onClick={load} disabled={loading} data-testid="analytics-refresh"
              className="inline-flex items-center gap-2 h-10 px-3 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-xs font-bold disabled:opacity-50">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={exportCSV} data-testid="analytics-export"
              className="inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-200 bg-white text-slate-700 text-xs font-bold hover:bg-slate-50">
              <Download size={13} /> Export CSV
            </button>
          </div>
        </div>

        {/* Hero: revenue + KPI row */}
        <div className="mt-6 grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-gradient-to-br from-[#1E1E3F] via-[#353564] to-indigo-900 text-white p-6 overflow-hidden relative">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 text-white/60 text-[10px] font-black tracking-wider uppercase">
                  <DollarSign size={12} /> Revenue paid out · last {days} days
                </div>
                <div className="mt-2 text-5xl font-black tracking-tight">
                  ${data.kpi.paid_value.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
                <div className="mt-2 flex items-center gap-3 text-xs">
                  <Delta v={data.deltas.paid_value} />
                  <span className="text-white/50">vs previous {days} days</span>
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-6 text-right">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-wider text-white/60">Avg payout</div>
                  <div className="text-xl font-black mt-1 tabular-nums">${data.kpi.avg_payout}</div>
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-wider text-white/60">Median</div>
                  <div className="text-xl font-black mt-1 tabular-nums">${data.kpi.median_payout}</div>
                </div>
              </div>
            </div>
            <div className="absolute inset-x-0 bottom-0 h-20 opacity-90">
              <Sparkline points={trend} color="#C9E9A4" height={80} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <KpiTile label="APPROVAL RATE" value={`${data.kpi.approval_rate}%`} delta={data.deltas.approval_rate} icon={Percent} bg="bg-emerald-50 border-emerald-200" numClass="text-emerald-700" iconBg="bg-emerald-200 text-emerald-800" />
            <KpiTile label="TOTAL SUBMITTED" value={data.total} delta={data.deltas.total} icon={BarChart3} bg="bg-sky-50 border-sky-200" numClass="text-sky-700" iconBg="bg-sky-200 text-sky-800" />
            <KpiTile label="UNIQUE CUSTOMERS" value={data.kpi.unique_customers} icon={Users} bg="bg-indigo-50 border-indigo-200" numClass="text-indigo-700" iconBg="bg-indigo-200 text-indigo-800" />
            <KpiTile label="REPEAT RATE" value={`${data.kpi.repeat_rate}%`} icon={Repeat} bg="bg-amber-50 border-amber-200" numClass="text-amber-700" iconBg="bg-amber-200 text-amber-800" />
          </div>
        </div>

        {/* Metric cards */}
        <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard label="APPROVED" value={data.counts.approved} icon={CheckCircle2} tone="green" />
          <MetricCard label="PAID" value={data.counts.paid} delta={data.deltas.paid} icon={DollarSign} tone="violet" />
          <MetricCard label="UNDER REVIEW" value={data.counts.under_review} icon={Zap} tone="amber" />
          <MetricCard label="REJECTED" value={data.counts.rejected} delta={data.deltas.rejected} invertDelta icon={XCircle} tone="red" />
        </div>

        {/* Conversion Funnel */}
        <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-black text-slate-900">Conversion Funnel</h3>
              <p className="text-xs text-slate-500 mt-0.5">Submitted → Approved → Paid drop-off · this window</p>
            </div>
            <span className="text-[10px] font-black tracking-wider text-slate-400 uppercase">Overall conversion: <span className="text-emerald-600">{data.funnel[3].value && data.funnel[0].value ? Math.round(data.funnel[3].value / data.funnel[0].value * 100) : 0}%</span></span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {data.funnel.map((f, i) => {
              const pct = funnelMax ? Math.round((f.value / funnelMax) * 100) : 0;
              const dropoff = i > 0 && data.funnel[i - 1].value ? Math.round(((data.funnel[i - 1].value - f.value) / data.funnel[i - 1].value) * 100) : null;
              const map = { sky: "from-sky-400 to-sky-600 text-white", amber: "from-amber-400 to-amber-600 text-white", emerald: "from-emerald-400 to-emerald-600 text-white", violet: "from-violet-400 to-violet-600 text-white" };
              return (
                <div key={f.label} className="relative">
                  {i > 0 && <div className="hidden md:block absolute -left-2 top-1/2 -translate-y-1/2 z-10"><ArrowRight size={14} className="text-slate-400" /></div>}
                  <div className={`rounded-xl p-4 bg-gradient-to-br ${map[f.color]} shadow-sm`}>
                    <div className="text-[10px] font-black uppercase tracking-wider opacity-90">{f.label}</div>
                    <div className="mt-2 text-3xl font-black tabular-nums">{f.value}</div>
                    <div className="mt-2 h-1.5 bg-white/25 rounded-full overflow-hidden">
                      <div className="h-full bg-white/70" style={{ width: `${pct}%` }} />
                    </div>
                    {dropoff !== null && dropoff > 0 && (
                      <div className="text-[10px] mt-2 font-bold opacity-80">–{dropoff}% dropped</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top brands + Peak hours heatmap */}
        <div className="mt-6 grid lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-black text-slate-900 flex items-center gap-2"><Award size={14} className="text-amber-500" /> Top Card Brands</h3>
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">by count</span>
            </div>
            <ul className="space-y-3">
              {data.top_brands.map((b, i) => {
                const pct = (b.count / brandMax) * 100;
                return (
                  <li key={b.brand} data-testid={`brand-row-${i}`} className="flex items-center gap-3">
                    <div className={`w-1.5 h-6 rounded ${BRAND_COLORS[i % BRAND_COLORS.length]}`} />
                    <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-600 text-[10px] font-black flex items-center justify-center">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-black text-slate-800 truncate">{b.brand}</div>
                        <div className="text-xs font-black text-slate-700 tabular-nums">{b.count}</div>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className={`h-full ${BRAND_COLORS[i % BRAND_COLORS.length]}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <div className="text-[11px] font-mono text-emerald-700 font-black tabular-nums w-16 text-right">${b.value.toLocaleString()}</div>
                  </li>
                );
              })}
              {!data.top_brands.length && <li className="text-sm text-slate-400 text-center py-6">No brand data yet.</li>}
            </ul>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-black text-slate-900 flex items-center gap-2"><Sparkles size={14} className="text-indigo-500" /> Peak submission hours</h3>
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">7×24 heatmap</span>
            </div>
            <div className="overflow-x-auto">
              <table className="border-separate border-spacing-[2px]">
                <thead>
                  <tr>
                    <th className="w-8"></th>
                    {Array.from({ length: 24 }).map((_, h) => (
                      <th key={h} className="w-4 h-4 text-[8px] font-black text-slate-400 text-center">{h % 4 === 0 ? h : ""}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {DOW.map((d, i) => (
                    <tr key={d}>
                      <td className="text-[10px] font-black text-slate-500 pr-1">{d}</td>
                      {Array.from({ length: 24 }).map((_, h) => {
                        const v = data.peak_hours?.[i]?.[h] || 0;
                        const opacity = v ? Math.max(0.15, v / peakMax) : 0;
                        return (
                          <td key={h} className="w-4 h-4 rounded" title={`${d} ${h}:00 — ${v} submission${v === 1 ? "" : "s"}`}
                            style={{ background: v ? `rgba(99,106,195,${opacity})` : "rgba(226,232,240,0.5)" }}
                          />
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-3 flex items-center gap-2 text-[10px] text-slate-400 font-black">
                <span>less</span>
                {[0.2, 0.4, 0.6, 0.8, 1].map((op) => <span key={op} className="w-4 h-3 rounded" style={{ background: `rgba(99,106,195,${op})` }} />)}
                <span>more</span>
              </div>
            </div>
          </div>
        </div>

        {/* Daily Submissions matrix */}
        <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
            <div>
              <h3 className="text-xl font-black text-slate-900">Daily Submissions</h3>
              <p className="text-sm text-slate-500 mt-1">Card-level activity per day · {data.daily.length} day{data.daily.length === 1 ? "" : "s"} tracked</p>
            </div>
          </div>

          {/* Totals strip */}
          <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
            {[
              { label: "TOTAL", val: data.daily_totals.total, color: "text-slate-900" },
              { label: "UNIQUE", val: data.daily_totals.unique, color: "text-sky-600" },
              { label: "DUPES", val: data.daily_totals.dupes, color: "text-orange-500" },
              { label: "APPROVED", val: data.daily_totals.approved, color: "text-emerald-600" },
              { label: "PAID", val: data.daily_totals.paid, color: "text-violet-600" },
              { label: "PENDING", val: data.daily_totals.pending, color: "text-amber-500" },
              { label: "REJECTED", val: data.daily_totals.rejected, color: "text-rose-500" },
              { label: "PAYOUT", val: `$${(data.daily_totals.payout || 0).toLocaleString()}`, color: "text-emerald-700" },
            ].map((t) => (
              <div key={t.label} className="rounded-xl border border-slate-200 p-4 text-center">
                <div className={`text-2xl font-black ${t.color} tabular-nums`}>{t.val}</div>
                <div className="text-[10px] font-black tracking-wider text-slate-400 mt-1">{t.label}</div>
              </div>
            ))}
          </div>

          {/* Matrix table */}
          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left py-3 px-2 font-black">Date</th>
                  <th className="font-black text-sky-600">Total</th>
                  <th className="font-black text-sky-600">Unique</th>
                  <th className="font-black text-orange-500">Dupes</th>
                  <th className="font-black text-emerald-600">Approved</th>
                  <th className="font-black text-violet-600">Paid</th>
                  <th className="font-black text-amber-500">Pending</th>
                  <th className="font-black text-rose-500">Rejected</th>
                  <th className="text-right font-black">Value</th>
                  <th className="text-right font-black text-emerald-700 pr-3">Payout</th>
                </tr>
              </thead>
              <tbody>
                {data.daily.map((r) => (
                  <tr key={r.date} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`daily-row-${r.date}`}>
                    <td className="py-3 px-2 text-slate-700 font-bold whitespace-nowrap">{fmtDayLabel(r.date)}</td>
                    <td className="text-center"><Bubble n={r.total} color="sky" /></td>
                    <td className="text-center"><Bubble n={r.unique} color="sky" muted /></td>
                    <td className="text-center"><Bubble n={r.dupes} color="orange" muted /></td>
                    <td className="text-center"><Bubble n={r.approved} color="emerald" muted /></td>
                    <td className="text-center"><Bubble n={r.paid} color="violet" muted /></td>
                    <td className="text-center"><Bubble n={r.pending} color="amber" muted /></td>
                    <td className="text-center"><Bubble n={r.rejected} color="rose" muted /></td>
                    <td className="text-right font-mono text-slate-700 tabular-nums">${r.value.toLocaleString()}</td>
                    <td className="text-right pr-3 font-mono text-emerald-700 font-black tabular-nums">${(r.payout || 0).toLocaleString()}</td>
                  </tr>
                ))}
                {!data.daily.length && <tr><td colSpan={10} className="py-8 text-center text-slate-400">No submissions in this window</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function KpiTile({ label, value, delta, icon: Icon, bg, numClass, iconBg }) {
  return (
    <div className={`rounded-2xl border p-4 ${bg}`}>
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconBg}`}><Icon size={13} strokeWidth={2.5} /></div>
        <span className="text-[9px] font-black tracking-wider text-slate-500">{label}</span>
      </div>
      <div className={`mt-2 text-2xl font-black tabular-nums ${numClass}`}>{value}</div>
      {delta !== undefined && <div className="mt-1"><Delta v={delta} /></div>}
    </div>
  );
}

function MetricCard({ label, value, delta, invertDelta, icon: Icon, tone }) {
  const map = {
    green: { bg: "bg-emerald-50 border-emerald-200", ring: "text-emerald-700", num: "text-emerald-700", iconBg: "bg-emerald-200 text-emerald-800" },
    violet: { bg: "bg-violet-50 border-violet-200", ring: "text-violet-700", num: "text-violet-700", iconBg: "bg-violet-200 text-violet-800" },
    amber: { bg: "bg-amber-50 border-amber-200", ring: "text-amber-700", num: "text-amber-700", iconBg: "bg-amber-200 text-amber-800" },
    red: { bg: "bg-rose-50 border-rose-200", ring: "text-rose-700", num: "text-rose-700", iconBg: "bg-rose-200 text-rose-800" },
  }[tone] || {};
  return (
    <div className={`rounded-2xl border p-6 ${map.bg}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${map.iconBg}`}><Icon size={16} strokeWidth={2.5} /></div>
          <span className={`text-[10px] font-black tracking-wider ${map.ring}`}>{label}</span>
        </div>
        {delta !== undefined && <Delta v={delta} invert={invertDelta} />}
      </div>
      <div className={`mt-3 text-4xl font-black ${map.num} tabular-nums`}>{value}</div>
    </div>
  );
}
